import React, { createContext, useCallback, useRef, useState } from 'react';

import { Toast } from './Toast';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export type ToastAction = Readonly<{
	label: string | React.ReactNode;
	onClick: () => void;
}>;

export type ToastConfig = Readonly<{
	variant: ToastVariant;
	message: string | React.ReactNode;
	action?: ToastAction;
	bottomPosition?: string;
	duration?: number;
}>;

export type ToastContextValue = {
	showToast: (config: ToastConfig) => void;
	hideToast: () => void;
};

export const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider: React.FC<Readonly<{ children: React.ReactNode }>> = ({ children }) => {
	const [activeToast, setActiveToast] = useState<ToastConfig | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const hideToast = useCallback(() => {
		setActiveToast(null);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const showToast = useCallback((config: ToastConfig) => {
		// Clear any existing timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		setActiveToast(config);

		// Auto-hide after duration (default 5 seconds)
		const duration = config.duration ?? 5000;
		timeoutRef.current = setTimeout(() => {
			setActiveToast(null);
			timeoutRef.current = null;
		}, duration);
	}, []);

	const handleActionClick = () => {
		activeToast?.action?.onClick();
		hideToast();
	};

	return (
		<ToastContext.Provider value={{ showToast, hideToast }}>
			{children}
			{/* Toast container - fixed at bottom */}
			{activeToast && (
				<Toast
					variant={activeToast.variant}
					message={activeToast.message}
					action={activeToast.action}
					bottomPosition={activeToast.bottomPosition}
					onActionClick={handleActionClick}
					onClose={hideToast}
				/>
			)}
		</ToastContext.Provider>
	);
};
